import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedService } from 'app/shared/shared.service';
import { ApiResponse, ValidationErrorPayload } from 'app/app.types';
import { API_MESSAGE_DISPLAY } from 'app/shared/interceptors/api-message-context';
import { WorkspaceLifecycleService } from 'app/shared/lifecycle/workspace-lifecycle.service';
import { WorkspaceLifecycle } from 'app/app.types';

@Injectable()
export class ApiResponseInterceptor implements HttpInterceptor {

    constructor(
        private _sharedService: SharedService,
        private _router: Router,
        private _lifecycle: WorkspaceLifecycleService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const show = req.context.get(API_MESSAGE_DISPLAY);

        return next.handle(req).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    const body = event.body as ApiResponse;

                    if (show) {
                        this._sharedService.show_message(body.message, body.status);
                    }

                    // Return the payload for successful requests
                    if (body && body.status === 'success') {
                        return event.clone({ body: body.payload });
                    }

                    // Check if response indicates alert
                    if (body && body.status === 'alert') {
                        return event.clone({ body: body.payload });
                    }

                    // Check if response indicates failure
                    if (body && body.status === 'error') {
                        // Throw error with formatted message
                        throw new HttpErrorResponse({
                            error: body,
                            headers: event.headers,
                            status: body.httpCode || event.status,
                            statusText: body.title || event.statusText,
                            url: event.url || undefined
                        });
                    }
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                const recoveredApiResponse = this._recoverApiResponseFromParseError(error);
                if (recoveredApiResponse) {
                    if (recoveredApiResponse.status === 'success' || recoveredApiResponse.status === 'alert') {
                        if (show && recoveredApiResponse.message) {
                            this._sharedService.show_message(recoveredApiResponse.message, recoveredApiResponse.status as any);
                        }

                        return of(new HttpResponse({
                            body: recoveredApiResponse.payload,
                            headers: error.headers,
                            status: 200,
                            statusText: recoveredApiResponse.title || 'OK',
                            url: error.url || undefined
                        }));
                    }

                    if (recoveredApiResponse.status === 'error') {
                        const errorMessages = this.extractErrorMessages(recoveredApiResponse as ApiResponse<ValidationErrorPayload>);
                        const message = errorMessages.length > 0
                            ? errorMessages.join('<br/> ')
                            : (recoveredApiResponse.message || 'An error occured, please try again.');

                        if (show) {
                            this._sharedService.show_message(message, 'error');
                        }

                        return throwError(() => ({
                            ...error,
                            message,
                            apiResponse: recoveredApiResponse
                        }));
                    }
                }

                if (error.error && typeof error.error === 'object') {
                    const apiError = error.error as ApiResponse;


                if (apiError.status === 'error' && apiError.code) {
                    const lifecycle = (apiError.payload as { lifecycle?: WorkspaceLifecycle })?.lifecycle ?? null;
                    if (apiError.code === 'WORKSPACE_BLOCKED') {
                        this._lifecycle.notifyBlocked(apiError.message || 'Workspace blocked.', lifecycle);
                    }
                    if (apiError.code === 'WORKSPACE_READONLY') {
                        this._lifecycle.notifyReadonly(apiError.message || 'Workspace is read-only.');
                    }
                }

                    if (apiError.title === 'Invalid token') {
                        window.location.assign('/auth/sign-out');
                    }

                    if (apiError.payload) {
                        const errorMessages = this.extractErrorMessages(apiError);
                        let message = errorMessages.length > 0 ? errorMessages.join('<br/> ') : apiError.message;

                        if (error.error.status === 'error') {
                            if (show) {
                                this._sharedService.show_message(message, "error");
                            }
                            this._sharedService.register_error(error);
                            // Return error with formatted message
                            return throwError(() => ({
                                ...error,
                                message: message,
                                apiResponse: apiError
                            }));
                        } else if (error.error.status === 'alert') {
                            if (show) {
                                this._sharedService.show_message(message, "alert");
                            }
                            return of(new HttpResponse({
                                body: apiError.payload,
                                headers: error.headers,
                                status: 200,
                                statusText: apiError.title || 'OK',
                                url: error.url || undefined
                            }));
                        }
                    }
                }

                if (error) {
                    this._sharedService.register_error(error);
                }
                if (show) {
                    this._sharedService.show_message("An error occured, please try again.", "error");
                }
                return throwError(() => error);
            })
        );
    }

    private _recoverApiResponseFromParseError(error: HttpErrorResponse): ApiResponse | null {
        const parseErrorText = (error as any)?.error?.text;
        if (!parseErrorText || typeof parseErrorText !== 'string') {
            return null;
        }

        const jsonStart = parseErrorText.indexOf('{"id"');
        if (jsonStart < 0) {
            return null;
        }

        const rawJson = parseErrorText.slice(jsonStart);
        try {
            const parsed = JSON.parse(rawJson);
            if (parsed && typeof parsed === 'object' && typeof parsed.status === 'string') {
                return parsed as ApiResponse;
            }
        } catch (e) {
            return null;
        }

        return null;
    }

    /**
     * Extract error messages from API response payload
     */
    private extractErrorMessages(response: ApiResponse<ValidationErrorPayload>): string[] {
        if (!response.payload) {
            return response.message ? [response.message] : [];
        }

        const messages: string[] = Object.entries(response.payload)
            .filter(([key]) => key !== 'error_id')
            .map(([_, field]) => {
                if (typeof field === 'object' && field.message) {
                    let message = field.message;
                    if (Array.isArray(field)) {
                        for (const v of field) {
                            const val = v.expectation ?? v.received ?? '';
                            message = message.replace(`%${v.name}%`, val);
                        }
                    }
                    return message;
                } else if (typeof field === 'string') {
                    return field;
                }
                return null;
            })
            .filter((msg): msg is string => !!msg);

        if (messages.length === 0 && response.message) {
            messages.push(response.message);
        }
        return messages;
    }
}
